import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions, Button, TextField, Box, Typography,
  IconButton, FormControlLabel, Switch, Alert, Snackbar, Tabs, Tab, Card, Select, MenuItem, FormControl,
  InputLabel, Divider, Tooltip, List, ListItem, ListItemText, FormHelperText,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import AddIcon from '@mui/icons-material/Add';
import useIsMobile from '../hooks/useIsMobile';
import QuestionCard from '../formulaires/QuestionCard';
import ConditionEditor from '../formulaires/ConditionEditor';
import ApercuStatique from '../formulaires/ApercuStatique';
import TesterFormulaireDialog from '../formulaires/TesterFormulaireDialog';
import type { FormulaireModeleDialogProps } from '../formulaires/props';
import {
  Condition, Definition, ModeleInput, Question, Section, TypeQuestion,
  TYPES, LIBELLES_TYPES, LIBELLES_OPERATEURS, LIMITES, OPERATEURS_PAR_TYPE, OPERATEURS_SANS_VALEUR,
  cheminQuestion, cheminSection, definitionVide, filtrerErreurs, nouvelleQuestion, nouvelleSection, optionsCible,
  porteursDeCondition, porteursDeValeur, questionsAnterieures, retirerConditionsOrphelines, retirerValeurDesConditions,
  idUnique, slugify, tousLesIds,
  creerModele, majModele, erreursParChemin, messageErreurs,
} from '../services/formulairesServices';

// Constructeur d'un modèle de formulaire : champs généraux, sections, questions (QuestionCard), conditions
// (ConditionEditor), aperçu statique / JSON à droite (onglets sur mobile). L'état local est une Definition complète
// (§3.2 du plan) ; les ids sont générés par slugify du titre / libellé, jamais saisis : ils suivent le texte (à la sortie
// du champ) tant que le modèle n'a jamais été enregistré, puis sont figés.
// Enregistrement : POST/PUT /formulaires/modeles ; un 400 renvoie { message, erreurs: [{ chemin, message }] } dont
// les chemins ('sections[i].questions[j].libelle'…) sont redistribués aux cartes. Un contrôle local reproduit les
// règles les plus courantes de schema.js pour guider avant l'appel réseau (le backend reste l'autorité).

type Onglet = 'editeur' | 'apercu' | 'json';

interface Confirmation {
  titre: string;
  texte: string;
  details?: string[];
  libelleBouton: string;
  action: () => void;
}

// Échange deux éléments voisins (sans effet hors bornes)
const deplacer = <T,>(arr: T[], i: number, delta: -1 | 1): T[] => {
  const j = i + delta;
  if (j < 0 || j >= arr.length) return arr;
  const r = arr.slice();
  const t = r[i]; r[i] = r[j]; r[j] = t;
  return r;
};

const nbQuestionsDe = (def: Definition): number => def.sections.reduce((n, s) => n + s.questions.length, 0);

// Contrôle local (sous-ensemble de validerDefinition de schema.js, mêmes chemins et messages proches) : sections
// et libellés vides, options manquantes, min > max, cibles de condition non antérieures / opérateur incompatible /
// valeur absente ou hors options. Renvoie les erreurs par chemin, comme erreursParChemin.
const validerLocalement = (def: Definition, nom: string): Record<string, string> => {
  const r: Record<string, string> = {};
  const err = (chemin: string, message: string) => { if (r[chemin] === undefined) r[chemin] = message; };
  if (!nom.trim()) err('nom', 'Le nom est obligatoire.');
  if (!def.sections.length) err('sections', 'sections : au moins une section attendue.');

  const controlerCondition = (c: Condition, chemin: string, candidates: Question[]) => {
    const cible = candidates.filter((q) => q.id === c.question)[0];
    if (!cible) {
      err(chemin + '.question', `condition : la cible « ${c.question} » doit être une question antérieure (hors paragraphes d'information).`);
      return;
    }
    if (OPERATEURS_PAR_TYPE[cible.type].indexOf(c.op) < 0) {
      err(chemin + '.op', `condition : l'opérateur « ${LIBELLES_OPERATEURS[c.op] || c.op} » n'est pas compatible avec une cible de type ${LIBELLES_TYPES[cible.type]}.`);
      return;
    }
    if (OPERATEURS_SANS_VALEUR.indexOf(c.op) >= 0) return;
    const valeurs = Array.isArray(c.valeur) ? c.valeur : c.valeur === undefined ? [] : [c.valeur];
    if (!valeurs.length || valeurs.some((v) => !v)) { err(chemin + '.valeur', 'condition : valeur attendue.'); return; }
    const options = optionsCible(cible);
    if (options) {
      const inconnue = valeurs.filter((v) => !options.some((o) => o.valeur === v))[0];
      if (inconnue !== undefined) err(chemin + '.valeur', `condition : « ${inconnue} » n'est pas (ou plus) une option de la question cible.`);
    }
  };

  def.sections.forEach((s, i) => {
    const ps = cheminSection(i);
    if (!s.titre.trim()) err(ps + '.titre', 'titre obligatoire.');
    if (s.condition) controlerCondition(s.condition, ps + '.condition', questionsAnterieures(def, { section: i }));
    if (!s.questions.length) err(ps + '.questions', 'questions : au moins une question attendue.');
    s.questions.forEach((q, j) => {
      const pq = cheminQuestion(i, j);
      if (!q.libelle.trim()) err(pq + '.libelle', 'libellé obligatoire.');
      if ((q.type === 'choix' || q.type === 'choix_multiple') && !(q.options && q.options.length)) err(pq + '.options', 'options : au moins une option attendue.');
      if (q.type === 'fichier' && !(q.accept && q.accept.length)) err(pq + '.accept', "accept : liste d'extensions attendue.");
      if (q.type === 'nombre' && q.min !== undefined && q.max !== undefined && q.min > q.max) err(pq + '.min', `min (${q.min}) doit être inférieur ou égal à max (${q.max}).`);
      if (q.condition) controlerCondition(q.condition, pq + '.condition', questionsAnterieures(def, { section: i, question: j }));
    });
  });
  return r;
};

// Nombre de conditions dont la cible n'est plus antérieure (sert à refuser un déplacement qui casserait l'ordre)
const nbCiblesHorsOrdre = (def: Definition): number =>
  Object.keys(validerLocalement(def, 'x')).filter((k) => /\.condition\.question$/.test(k)).length;

// Ligne « Ajouter une question » d'une section : type + libellé (l'id est généré depuis le libellé, une fois)
const AjoutQuestion: React.FC<{ disabled: boolean; onAdd: (type: TypeQuestion, libelle: string) => void }> = ({ disabled, onAdd }) => {
  const [type, setType] = useState<TypeQuestion>('texte');
  const [libelle, setLibelle] = useState('');
  const ajouter = () => {
    if (disabled) return;
    onAdd(type, libelle.trim());
    setLibelle('');
  };
  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Type</InputLabel>
          <Select label="Type" value={type} onChange={(e) => setType(e.target.value as TypeQuestion)}>
            {TYPES.map((t) => (
              <MenuItem key={t} value={t}>{LIBELLES_TYPES[t]}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          size="small"
          label={type === 'info' ? 'Texte du paragraphe' : 'Libellé de la question'}
          sx={{ flex: 1, minWidth: 200 }}
          inputProps={{ maxLength: LIMITES.libelle }}
          value={libelle}
          onChange={(e) => setLibelle(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); ajouter(); } }}
        />
        <Button variant="outlined" startIcon={<AddIcon />} onClick={ajouter} disabled={disabled} sx={{ whiteSpace: 'nowrap' }}>
          Ajouter une question
        </Button>
      </Box>
      <Typography variant="caption" color="text.secondary">
        {disabled
          ? `${LIMITES.questions} questions maximum par formulaire`
          : "L'identifiant de la question est généré depuis le libellé ; il est figé dès le premier enregistrement du formulaire."}
      </Typography>
    </Box>
  );
};

const FormulaireModeleDialog: React.FC<FormulaireModeleDialogProps> = ({ open, modele, onClose, onSaved }) => {
  const isMobile = useIsMobile();
  const [nom, setNom] = useState('');
  const [description, setDescription] = useState('');
  const [actif, setActif] = useState(true);
  const [def, setDef] = useState<Definition>(definitionVide);
  const [ongletDroite, setOngletDroite] = useState<Onglet>('apercu');
  const [ongletMobile, setOngletMobile] = useState<Onglet>('editeur');
  const [tentative, setTentative] = useState(false); // un enregistrement a été tenté : le contrôle local s'affiche en direct
  const [erreursServeur, setErreursServeur] = useState<Record<string, string>>({});
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<{ msg: string; sev: 'success' | 'error' | 'info' } | null>(null);
  // Aperçu interactif (lot 4b) sur la définition courante, même non enregistrée
  const [testOuvert, setTestOuvert] = useState(false);
  const initial = useRef('');
  // Clés React des cartes (sections, questions) : l'id, sauf pour un élément dont l'id a été régénéré, qui garde une
  // clé stable (k:n) pour ne pas être remonté — un remontage pendant le blur ferait perdre le focus au champ suivant
  const clesReact = useRef<Record<string, string>>({});
  const compteurCle = useRef(0);
  const cleReact = (id: string): string => clesReact.current[id] || id;
  // false pendant le premier rendu d'une ouverture (état pas encore réinitialisé) : `modifie` est alors ignoré
  const [pret, setPret] = useState(false);

  const instantane = (n: string, d: string, a: boolean, f: Definition): string => JSON.stringify({ n, d, a, f });

  // Initialisation à chaque ouverture : modèle existant (copie profonde de sa définition) ou définition vide
  useEffect(() => {
    if (!open) { setPret(false); return; }
    const n = modele ? modele.nom || '' : '';
    const d = modele ? modele.description || '' : '';
    const a = modele ? Number(modele.actif) === 1 : true;
    const f: Definition = modele && modele.definition && Array.isArray(modele.definition.sections)
      ? (JSON.parse(JSON.stringify(modele.definition)) as Definition)
      : definitionVide();
    setNom(n); setDescription(d); setActif(a); setDef(f);
    initial.current = instantane(n, d, a, f);
    setTentative(false);
    setErreursServeur({});
    setConfirmation(null);
    setOngletMobile('editeur');
    clesReact.current = {};
    setPret(true);
  }, [open, modele]);

  const modifie = pret && instantane(nom, description, actif, def) !== initial.current;

  // Erreurs affichées : contrôle local (après une première tentative) + réponse 400 du serveur (jusqu'à la prochaine
  // modification)
  const erreursLocales = useMemo(() => (tentative ? validerLocalement(def, nom) : {}), [tentative, def, nom]);
  const erreurs = useMemo(() => ({ ...erreursLocales, ...erreursServeur }), [erreursLocales, erreursServeur]);

  const changerDef = (suivante: Definition) => {
    setDef(suivante);
    if (Object.keys(erreursServeur).length) setErreursServeur({});
  };
  const majSection = (i: number, patch: Partial<Section>) =>
    changerDef({ ...def, sections: def.sections.map((s, idx) => (idx === i ? { ...s, ...patch } : s)) });
  const majQuestion = (i: number, j: number, q: Question) =>
    majSection(i, { questions: def.sections[i].questions.map((x, idx) => (idx === j ? q : x)) });

  // --- Ids : tant que le modèle n'a jamais été enregistré, l'id suit le titre / libellé (régénéré à la sortie du champ,
  // pas pendant la frappe : c'est la clé React de la carte). Une question visée par une condition garde son id.
  // Après le premier enregistrement, les ids sont figés (§3.2 du plan).
  const transfererCle = (ancien: string, nouveau: string) => {
    clesReact.current[nouveau] = clesReact.current[ancien] || `k:${++compteurCle.current}`;
    delete clesReact.current[ancien];
  };
  const regenererIdSection = (i: number) => {
    if (modele) return;
    const s = def.sections[i];
    const autres = tousLesIds(def).filter((id) => id !== s.id);
    const id = idUnique(s.titre.trim() ? slugify(s.titre, 's_') : 'section', autres);
    if (id === s.id) return;
    transfererCle(s.id, id);
    majSection(i, { id });
  };
  const regenererIdQuestion = (i: number, j: number) => {
    if (modele) return;
    const q = def.sections[i].questions[j];
    if (porteursDeCondition(def, q.id).length) return;
    const autres = tousLesIds(def).filter((id) => id !== q.id);
    const id = idUnique(q.libelle.trim() ? slugify(q.libelle) : `q_${q.type}`, autres);
    if (id === q.id) return;
    transfererCle(q.id, id);
    majQuestion(i, j, { ...q, id });
  };

  // --- Sections
  const ajouterSection = () => {
    if (def.sections.length >= LIMITES.sections) return;
    changerDef({ ...def, sections: [...def.sections, nouvelleSection(tousLesIds(def), `Section ${def.sections.length + 1}`)] });
  };
  const deplacerSection = (i: number, delta: -1 | 1) => {
    const suivante = { ...def, sections: deplacer(def.sections, i, delta) };
    if (nbCiblesHorsOrdre(suivante) > nbCiblesHorsOrdre(def)) {
      setToast({ msg: 'Déplacement impossible : une condition viserait une question qui ne serait plus antérieure. Retirez-la d\'abord.', sev: 'error' });
      return;
    }
    changerDef(suivante);
  };
  const supprimerSection = (i: number) => {
    if (def.sections.length <= 1) return;
    const s = def.sections[i];
    const executer = () => {
      let suivante: Definition = { ...def, sections: def.sections.filter((_, idx) => idx !== i) };
      s.questions.forEach((q) => { suivante = retirerConditionsOrphelines(suivante, q.id); });
      changerDef(suivante);
      setConfirmation(null);
    };
    if (!s.questions.length) { executer(); return; }
    // Conditions d'autres sections qui visent une question de celle-ci
    const dependants: string[] = [];
    s.questions.forEach((q) => {
      porteursDeCondition(def, q.id).forEach((p) => {
        if (p.type === 'section' || !s.questions.some((x) => x.id === p.id)) {
          dependants.push(`${p.type === 'section' ? 'Section' : 'Question'} « ${p.libelle || p.id} » (condition sur « ${q.libelle || q.id} »)`);
        }
      });
    });
    setConfirmation({
      titre: 'Supprimer la section ?',
      texte: `La section « ${s.titre || s.id} » et ses ${s.questions.length} question(s) seront supprimées.`
        + (dependants.length ? ' Les conditions suivantes, qui en dépendent, seront retirées :' : ''),
      details: dependants,
      libelleBouton: 'Supprimer',
      action: executer,
    });
  };

  // --- Questions
  const ajouterQuestion = (i: number, type: TypeQuestion, libelle: string) => {
    if (nbQuestionsDe(def) >= LIMITES.questions) return;
    const q = nouvelleQuestion(type, libelle || undefined, tousLesIds(def));
    majSection(i, { questions: [...def.sections[i].questions, q] });
  };
  const deplacerQuestion = (i: number, j: number, delta: -1 | 1) => {
    const suivante = { ...def, sections: def.sections.map((s, idx) => (idx === i ? { ...s, questions: deplacer(s.questions, j, delta) } : s)) };
    if (nbCiblesHorsOrdre(suivante) > nbCiblesHorsOrdre(def)) {
      setToast({ msg: 'Déplacement impossible : une condition viserait une question qui ne serait plus antérieure. Retirez-la d\'abord.', sev: 'error' });
      return;
    }
    changerDef(suivante);
  };
  const supprimerQuestion = (i: number, j: number) => {
    const q = def.sections[i].questions[j];
    const executer = () => {
      const sansQuestion: Definition = {
        ...def,
        sections: def.sections.map((s, idx) => (idx === i ? { ...s, questions: s.questions.filter((_, k) => k !== j) } : s)),
      };
      changerDef(retirerConditionsOrphelines(sansQuestion, q.id));
      setConfirmation(null);
    };
    const porteurs = porteursDeCondition(def, q.id);
    if (!porteurs.length) { executer(); return; }
    setConfirmation({
      titre: 'Supprimer la question ?',
      texte: `D'autres éléments s'affichent selon la réponse à « ${q.libelle || q.id} ». Leur condition sera retirée (ils seront toujours affichés) :`,
      details: porteurs.map((p) => `${p.type === 'section' ? 'Section' : 'Question'} « ${p.libelle || p.id} »`),
      libelleBouton: 'Supprimer',
      action: executer,
    });
  };

  // --- Options : la valeur d'une option supprimée peut être visée par des conditions (autres questions, sections) ;
  // elles sont ajustées (valeur retirée, condition supprimée si elle ne porte plus rien) après confirmation
  const supprimerOption = (i: number, j: number, k: number) => {
    const q = def.sections[i].questions[j];
    const options = q.options || [];
    const o = options[k];
    if (!o) return;
    const sansOption: Definition = {
      ...def,
      sections: def.sections.map((s, idx) => (idx === i
        ? { ...s, questions: s.questions.map((x, jdx) => (jdx === j ? { ...x, options: options.filter((_, kdx) => kdx !== k) } : x)) }
        : s)),
    };
    const porteurs = porteursDeValeur(def, q.id, o.valeur);
    if (!porteurs.length) { changerDef(sansOption); return; }
    setConfirmation({
      titre: "Retirer l'option ?",
      texte: `Des éléments s'affichent selon l'option « ${o.libelle || o.valeur} » de « ${q.libelle || q.id} ». Cette valeur sera `
        + 'retirée de leur condition (la condition disparaît si elle ne porte plus aucune valeur) :',
      details: porteurs.map((p) => `${p.type === 'section' ? 'Section' : 'Question'} « ${p.libelle || p.id} »`),
      libelleBouton: 'Retirer',
      action: () => {
        changerDef(retirerValeurDesConditions(sansOption, q.id, o.valeur));
        setConfirmation(null);
      },
    });
  };

  // --- Fermeture / enregistrement
  const demanderFermeture = () => {
    if (saving) return;
    if (!modifie) { onClose(); return; }
    setConfirmation({
      titre: 'Abandonner les modifications ?',
      texte: 'Les modifications non enregistrées seront perdues.',
      libelleBouton: 'Abandonner',
      action: () => { setConfirmation(null); onClose(); },
    });
  };

  const handleSave = async () => {
    setTentative(true);
    const locales = validerLocalement(def, nom);
    const nb = Object.keys(locales).length;
    if (nb) {
      setToast({ msg: nb === 1 ? 'Un point à corriger avant l\'enregistrement' : `${nb} points à corriger avant l'enregistrement`, sev: 'error' });
      if (isMobile) setOngletMobile('editeur');
      return;
    }
    setSaving(true);
    try {
      const corps: ModeleInput = { nom: nom.trim(), description: description.trim(), definition: def, actif };
      const m = modele ? await majModele(modele.id, corps) : await creerModele(corps);
      initial.current = instantane(nom, description, actif, def);
      onSaved(m);
      onClose();
    } catch (e) {
      console.error('Erreur enregistrement modèle de formulaire', e);
      setErreursServeur(erreursParChemin(e));
      setToast({ msg: messageErreurs(e, "Erreur lors de l'enregistrement"), sev: 'error' });
      if (isMobile) setOngletMobile('editeur');
    } finally {
      setSaving(false);
    }
  };

  // Erreurs de niveau définition qu'aucun champ n'affiche (schema, sections, definition…)
  const erreursGlobales = ['schema', 'sections', 'definition'].filter((k) => erreurs[k]).map((k) => erreurs[k]);
  const nbQuestions = nbQuestionsDe(def);
  const ongletActif: Onglet = isMobile ? ongletMobile : ongletDroite;
  const json = useMemo(() => JSON.stringify(def, null, 2), [def]);

  const editeur = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {modele && (
        <Alert severity="info" variant="outlined" sx={{ py: 0 }}>
          Modifier un formulaire n'affecte pas les formulaires déjà envoyés (copie figée) ; un formulaire joint à un
          dossier mais pas encore envoyé prendra la nouvelle version à l'envoi.
        </Alert>
      )}
      {erreursGlobales.length > 0 && <Alert severity="error">{erreursGlobales.join(' ')}</Alert>}

      {/* Champs généraux */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <TextField
          label="Nom du formulaire"
          required
          fullWidth
          size="small"
          inputProps={{ maxLength: LIMITES.nom }}
          value={nom}
          onChange={(e) => { setNom(e.target.value); if (erreursServeur.nom) setErreursServeur({}); }}
          error={!!erreurs.nom}
          helperText={erreurs.nom || 'Visible dans la liste, dans le dossier et dans le mail envoyé au client'}
        />
        <TextField
          label="Description interne (optionnel)"
          fullWidth
          size="small"
          inputProps={{ maxLength: LIMITES.description }}
          value={description}
          onChange={(e) => { setDescription(e.target.value); if (erreursServeur.description) setErreursServeur({}); }}
          error={!!erreurs.description}
          helperText={erreurs.description}
        />
        <TextField
          label="Introduction affichée au client (optionnel)"
          fullWidth
          multiline
          minRows={2}
          size="small"
          inputProps={{ maxLength: LIMITES.intro }}
          value={def.intro}
          onChange={(e) => changerDef({ ...def, intro: e.target.value })}
          error={!!erreurs.intro}
          helperText={erreurs.intro || (def.intro.length > LIMITES.intro - 200 ? `${def.intro.length}/${LIMITES.intro}` : undefined)}
        />
        <TextField
          label="Message de fin, après envoi (optionnel)"
          fullWidth
          multiline
          minRows={2}
          size="small"
          inputProps={{ maxLength: LIMITES.intro }}
          value={def.message_fin}
          onChange={(e) => changerDef({ ...def, message_fin: e.target.value })}
          error={!!erreurs.message_fin}
          helperText={erreurs.message_fin || (def.message_fin.length > LIMITES.intro - 200 ? `${def.message_fin.length}/${LIMITES.intro}` : undefined)}
        />
      </Box>

      {/* Sections */}
      {def.sections.map((s, i) => {
        const ps = cheminSection(i);
        const erreursCondition = filtrerErreurs(erreurs, ps + '.condition');
        const erreurCondition = Object.keys(erreursCondition).map((k) => erreursCondition[k]).join(' ') || undefined;
        const erreurSection = erreurs[ps] || erreurs[ps + '.id'] || erreurs[ps + '.questions'];
        return (
          <Card key={cleReact(s.id)} variant="outlined" sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, bgcolor: 'action.hover' }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
              <TextField
                label={`Section ${i + 1} — titre`}
                required
                fullWidth
                size="small"
                inputProps={{ maxLength: LIMITES.libelle }}
                value={s.titre}
                onChange={(e) => majSection(i, { titre: e.target.value })}
                onBlur={() => regenererIdSection(i)}
                error={!!erreurs[ps + '.titre']}
                helperText={erreurs[ps + '.titre'] || `id : ${s.id}`}
              />
              <Tooltip title="Monter la section">
                <span>
                  <IconButton size="small" onClick={() => deplacerSection(i, -1)} disabled={i === 0}>
                    <ArrowUpwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title="Descendre la section">
                <span>
                  <IconButton size="small" onClick={() => deplacerSection(i, 1)} disabled={i >= def.sections.length - 1}>
                    <ArrowDownwardIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              <Tooltip title={def.sections.length <= 1 ? 'Un formulaire contient au moins une section' : 'Supprimer la section'}>
                <span>
                  <IconButton size="small" color="error" onClick={() => supprimerSection(i)} disabled={def.sections.length <= 1}>
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>

            <ConditionEditor
              condition={s.condition}
              candidates={questionsAnterieures(def, { section: i })}
              onChange={(c) => {
                const { condition, ...reste } = s;
                changerDef({ ...def, sections: def.sections.map((x, idx) => (idx === i ? (c ? { ...reste, condition: c } : reste) : x)) });
              }}
              erreur={erreurCondition}
              libellePorteur={s.titre || s.id}
            />

            <Divider />

            {s.questions.length === 0 && (
              <Typography variant="body2" color={erreurSection ? 'error' : 'text.secondary'}>
                {erreurSection || 'Aucune question dans cette section pour le moment : ajoutez-en au moins une.'}
              </Typography>
            )}
            {s.questions.length > 0 && erreurSection && <FormHelperText error>{erreurSection}</FormHelperText>}

            {s.questions.map((q, j) => (
              <QuestionCard
                key={cleReact(q.id)}
                question={q}
                index={j}
                section={s}
                sectionIndex={i}
                definition={def}
                erreurs={erreurs}
                onChange={(nq) => majQuestion(i, j, nq)}
                onMove={(delta) => deplacerQuestion(i, j, delta)}
                onDelete={() => supprimerQuestion(i, j)}
                onDeleteOption={(k) => supprimerOption(i, j, k)}
                onLibelleBlur={() => regenererIdQuestion(i, j)}
              />
            ))}

            <AjoutQuestion disabled={nbQuestions >= LIMITES.questions} onAdd={(type, libelle) => ajouterQuestion(i, type, libelle)} />
          </Card>
        );
      })}

      <Box>
        <Button variant="outlined" startIcon={<AddIcon />} onClick={ajouterSection} disabled={def.sections.length >= LIMITES.sections}>
          Ajouter une section
        </Button>
        {def.sections.length >= LIMITES.sections && (
          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>{LIMITES.sections} sections maximum</Typography>
        )}
      </Box>
    </Box>
  );

  const panneauDroite = (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minWidth: 0 }}>
      {!isMobile && (
        <Tabs value={ongletDroite} onChange={(_, v) => setOngletDroite(v as Onglet)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tab value="apercu" label="Aperçu" />
          <Tab value="json" label="JSON" />
        </Tabs>
      )}
      <Box sx={{ flex: 1, overflow: 'auto', p: 2 }}>
        {ongletActif === 'json' ? (
          <Box component="pre" sx={{ m: 0, fontFamily: 'monospace', fontSize: 12, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
            {json}
          </Box>
        ) : (
          <ApercuStatique definition={def} />
        )}
      </Box>
    </Box>
  );

  return (
    <>
      <Dialog
        open={open}
        onClose={demanderFermeture}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        PaperProps={{ sx: isMobile ? {} : { height: '92vh' } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}>
          <Box sx={{ flexGrow: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {modele ? `Modifier « ${modele.nom} »` : 'Nouveau formulaire'}
            {modele && (
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                version {modele.version}
              </Typography>
            )}
          </Box>
          <FormControlLabel
            control={<Switch checked={actif} onChange={(e) => setActif(e.target.checked)} />}
            label={actif ? 'Actif' : 'Désactivé'}
          />
          <IconButton onClick={demanderFermeture} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {isMobile && (
          <Tabs value={ongletMobile} onChange={(_, v) => setOngletMobile(v as Onglet)} variant="fullWidth">
            <Tab value="editeur" label="Éditeur" />
            <Tab value="apercu" label="Aperçu" />
            <Tab value="json" label="JSON" />
          </Tabs>
        )}

        <DialogContent dividers sx={{ p: 0, display: 'flex', overflow: 'hidden' }}>
          {(!isMobile || ongletMobile === 'editeur') && (
            <Box sx={{ flex: 1, minWidth: 0, overflowY: 'auto', p: 2 }}>{editeur}</Box>
          )}
          {(!isMobile || ongletMobile !== 'editeur') && (
            <Box sx={{ width: isMobile ? '100%' : '40%', flexShrink: 0, borderLeft: isMobile ? 0 : 1, borderColor: 'divider', minWidth: 0 }}>
              {panneauDroite}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ gap: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ flexGrow: 1, pl: 1 }}>
            {def.sections.length} section{def.sections.length > 1 ? 's' : ''}, {nbQuestions} question{nbQuestions > 1 ? 's' : ''}
            {modifie ? ' — modifications non enregistrées' : ''}
          </Typography>
          <Button variant="outlined" onClick={() => setTestOuvert(true)} disabled={nbQuestions === 0}>
            {isMobile ? 'Tester' : 'Tester le formulaire'}
          </Button>
          <Button onClick={demanderFermeture} disabled={saving}>Annuler</Button>
          <Button variant="contained" onClick={handleSave} disabled={saving || !nom.trim()}>
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Aperçu interactif : définition courante de l'éditeur, rien n'est enregistré */}
      <TesterFormulaireDialog
        open={testOuvert}
        definition={def}
        nomModele={nom || 'Formulaire'}
        onClose={() => setTestOuvert(false)}
      />

      {/* Confirmation (suppression, abandon) */}
      <Dialog open={!!confirmation} onClose={() => setConfirmation(null)} maxWidth="sm" fullWidth>
        {confirmation && (
          <>
            <DialogTitle>{confirmation.titre}</DialogTitle>
            <DialogContent>
              <DialogContentText>{confirmation.texte}</DialogContentText>
              {confirmation.details && confirmation.details.length > 0 && (
                <List dense>
                  {confirmation.details.map((d, k) => (
                    <ListItem key={k} disableGutters>
                      <ListItemText primary={d} />
                    </ListItem>
                  ))}
                </List>
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setConfirmation(null)}>Retour</Button>
              <Button variant="contained" color="error" onClick={confirmation.action}>{confirmation.libelleBouton}</Button>
            </DialogActions>
          </>
        )}
      </Dialog>

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {toast ? <Alert severity={toast.sev} onClose={() => setToast(null)}>{toast.msg}</Alert> : undefined}
      </Snackbar>
    </>
  );
};

export default FormulaireModeleDialog;
